// File: components/Payments/FilteredPayments.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import SearchInput from '../shared/SearchInput';
import { SpinnerIcon } from '../shared/Icons';
import Toast from '../shared/Toast';
import { cssUrl } from '../../utils/cssVersion';
import HelpButton from '../shared/HelpButton';
import { useUser } from '../../utils/UserContext';
import { ABNModal, TFNModal } from '../shared/Modals';
import PaymentCard from './PaymentCard';
import MobileStatsCard from './MobileStatsCard';
import { fetchPayments } from '../../utils/api';
import { CHIP_GROUP_COLORS } from '../shared/chipColors';


const CHIPS = [
  { label: 'All',     color: CHIP_GROUP_COLORS.rose },
  { label: 'ABN',     color: CHIP_GROUP_COLORS.cyan },
  { label: 'TFN',     color: CHIP_GROUP_COLORS.cyan },
  { label: 'Current', color: CHIP_GROUP_COLORS.lime },
];

const FilteredPayments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeChip, setActiveChip] = useState('All');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [toast, setToast] = useState(null);
  const { user } = useUser();

  useEffect(() => {
    const link1 = document.createElement('link');
    link1.rel = 'stylesheet';
    link1.href = cssUrl('newuseronboarding.css');
    document.head.appendChild(link1);
    const link2 = document.createElement('link');
    link2.rel = 'stylesheet';
    link2.href = cssUrl('filterTemplate.css');
    document.head.appendChild(link2);
    const link3 = document.createElement('link');
    link3.rel = 'stylesheet';
    link3.href = cssUrl('toast.css');
    document.head.appendChild(link3);
    const link4 = document.createElement('link');
    link4.rel = 'stylesheet';
    link4.href = cssUrl('filteredPayments.css');
    document.head.appendChild(link4);
    return () => {
      document.head.removeChild(link1);
      document.head.removeChild(link2);
      document.head.removeChild(link3);
      document.head.removeChild(link4);
    };
  }, []);

  /* ── Fetch & combine payments ── */
  const getData = async () => {
    setLoading(true);
    try {
      const data = await fetchPayments();
      const all = [
        ...(data.abnPayments || []).map(p => {
          const arr = [...p];
          arr.type = 'ABN';
          return arr;
        }),
        ...(data.tfnPayments || []).map(p => {
          const arr = [...p];
          arr.type = 'TFN';
          return arr;
        }),
      ];
      setPayments(all);
    } catch (err) {
      console.error(err);
      setError('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { getData(); }, []);

  /* ── Most recent date for "Current" chip ── */
  const mostRecentDate = useMemo(() => {
    if (!payments.length) return null;
    const sorted = [...payments].sort((a, b) => new Date(b[2]) - new Date(a[2]));
    return new Date(sorted[0][2]).toISOString().split('T')[0];
  }, [payments]);

  /* ── Filtered payments based on active chip + search ── */
  const filtered = useMemo(() => {
    let result;
    switch (activeChip) {
      case 'ABN':
        result = payments.filter(p => p.type === 'ABN');
        break;
      case 'TFN':
        result = payments.filter(p => p.type === 'TFN');
        break;
      case 'Current':
        result = payments.filter(p => {
          const d = new Date(p[2]).toISOString().split('T')[0];
          return d === mostRecentDate;
        });
        break;
      default:
        result = payments;
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(p => {
        const name = (p.type === 'ABN' ? p[15] : p[16]) || '';
        const po = p.type === 'ABN' ? (p[4] || '') : '';
        const amount = p.type === 'ABN' ? (p[7] || '') : (p[4] || '');
        const status = p.type === 'ABN' ? (p[3] || '') : (p[17] || '');
        return (
          name.toLowerCase().includes(q) ||
          po.toLowerCase().includes(q) ||
          amount.toLowerCase().includes(q) ||
          status.toLowerCase().includes(q)
        );
      });
    }

    return result;
  }, [payments, activeChip, mostRecentDate, searchQuery]);

  /* ── Sorted by date descending ── */
  const sortedFiltered = useMemo(() => {
    return [...filtered].sort((a, b) => new Date(b[2]) - new Date(a[2]));
  }, [filtered]);

  /* ── Chip counts ── */
  const chipCounts = useMemo(() => ({
    ABN: payments.filter(p => p.type === 'ABN').length,
    TFN: payments.filter(p => p.type === 'TFN').length,
  }), [payments]);

  /* ── Modal handlers ── */
  const handleCardClick = useCallback((payment) => setSelectedPayment(payment), []);
  const closeModal = useCallback(() => setSelectedPayment(null), []);

  /* ── Loading state ── */
  if (loading) {
    return (
      <div style={s.loadingWrap}>
        <SpinnerIcon />
      </div>
    );
  }

  /* ── Error state ── */
  if (error) {
    return (
      <div style={s.loadingWrap}>
        <div style={s.errorText}>{error}</div>
      </div>
    );
  }

  return (
    <div style={s.wrapper}>
      {/* ── Header ── */}
      <div style={s.headerSection}>
        <h1 style={s.title}>Payments</h1>
        <p style={s.subtitle}>View and manage all payment records</p>
      </div>

      {/* ── Search ── */}
      <div style={s.searchWrap}>
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search name, PO, status..."
        />
      </div>

      {/* ── Filter Chips ── */}
      <div className="fp-sticky-chips" style={{ position: 'relative', zIndex: 1 }}>
        <div className="fp-chip-row">
          {CHIPS.map(({ label, color }) => {
            const active = activeChip === label;
            const inlineStyle = color
              ? {
                  background: active ? `${color}22` : undefined,
                  color: active ? color : undefined,
                  borderColor: active ? `${color}80` : `${color}90`,
                  boxShadow: active ? `0 0 8px ${color}40` : undefined,
                }
              : undefined;

            return (
              <button
                key={label}
                className={`fp-chip ${active ? 'fp-chip-active' : ''}`}
                onClick={() => setActiveChip(label)}
                style={inlineStyle}
              >
                {label}
                {(label === 'ABN' || label === 'TFN') && (
                  <span className="fp-chip-count">({chipCounts[label]})</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Stats Card ── */}
      <MobileStatsCard
        payments={filtered}
        activeChip={activeChip}
        helpButton={
          <HelpButton helpData={[
            { chip: 'payments_filter', urlFilter: '/static/pdffilters/PaymentsAll.pdf' }
          ]} />
        }
      />

      {/* ── Payment Cards ── */}
      {sortedFiltered.length === 0 ? (
        <div className="fp-empty">
          {searchQuery.trim()
            ? `No results for "${searchQuery}"`
            : 'No payments found'}
        </div>
      ) : (
        <div className="fp-card-list">
          {sortedFiltered.map((p, i) => (
            <PaymentCard
              key={`${p.type}-${p[0]}-${i}`}
              payment={p}
              onClick={() => handleCardClick(p)}
            />
          ))}
        </div>
      )}

      {/* ── Footer ── */}
      <p className="fp-footer">Payments</p>

      {/* ── Modals ── */}
      {selectedPayment && selectedPayment.type === 'ABN' && (
        <ABNModal
          data={selectedPayment}
          onClose={closeModal}
          onCreateInvoice={() => closeModal()}
          refreshPayments={getData}
        />
      )}
      {selectedPayment && selectedPayment.type === 'TFN' && (
        <TFNModal
          data={selectedPayment}
          onClose={closeModal}
          onToast={setToast}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

/* ── Inline styles matching formStyles / RecordMeetingForm pattern ── */
const s = {
  wrapper: {
    padding: "24px 20px 40px",
    width: "100%",
    boxSizing: "border-box",
    fontFamily: "'DM Sans', sans-serif",
    color: "#f1f5f9",
  },
  headerSection: {
    marginBottom: 16,
  },
  title: {
    fontSize: "1.15rem",
    fontWeight: 700,
    color: "#f1f5f9",
    marginBottom: 4,
    marginTop: 0,
  },
  subtitle: {
    fontSize: "0.8rem",
    color: "rgba(255,255,255,0.45)",
    marginBottom: 0,
    marginTop: 0,
  },
  searchWrap: {
    marginBottom: 12,
  },
  loadingWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 200,
  },
  errorText: {
    color: "#f87171",
    fontSize: "0.85rem",
  },
};

export default FilteredPayments;